/*
 Licensed Materials - Property of IBM
 5737-I23
 Copyright IBM Corp. 2019. All Rights Reserved.
 U.S. Government Users Restricted Rights:
 Use, duplication or disclosure restricted by GSA ADP Schedule
 Contract with IBM Corp.
 */
import React, { useEffect, useState } from 'react';

import { FormLabel } from 'carbon-components-react';

import './CustomFieldTemplate.scss';

function defaultUpdateValidity(errors, schema) {
    const updatedSchema = { ...schema };
    if (errors) {
        // client side validation error
        updatedSchema.invalid = true;
        updatedSchema.invalidText = errors[0];
    } else {
        updatedSchema.invalid = false;
        updatedSchema.invalidText = '';
    }
    return updatedSchema;
}

const fieldTemplateObject = (id, label, required, description, formContext, help, _children, fieldSchema) => {
    let fieldsetClassName = 'bx--fieldset';
    const isObjectWithNoPropNorAnyThing = (
        !Object.keys(fieldSchema).includes('properties')
        && !Object.keys(fieldSchema).includes('allOf')
        && !Object.keys(fieldSchema).includes('oneOf')
        && !Object.keys(fieldSchema).includes('anyOf')
    );
    if (isObjectWithNoPropNorAnyThing) {
        fieldsetClassName = `${fieldsetClassName} baiw--fieldset-no-prop`;
        return (
            <div className={fieldsetClassName}>
                {label && <FormLabel >{label}{!required && formContext && formContext.optionalLabel ? ` (${formContext.optionalLabel})` : null}</FormLabel>}
                {description && <div className="bx--form__helper-text" htmlFor={id}>{description}</div>}
                {formContext && formContext.getErrorTypeMessage && <div className="bx--form-requirement baiw--form-requirement-no-prop" htmlFor={id}>{formContext.getErrorTypeMessage(fieldSchema.type, formContext.messageObjectNoProp)}</div>}
                {help}
            </div>
        );
    } else {
        fieldsetClassName = (fieldSchema.title ? fieldsetClassName : `${fieldsetClassName} baiw--fieldset-no-title`);
        // By default react json schema set displayLabel to false for object field.
        // TitleField and DescriptionField are already in _children.
        return (
            <div className={fieldsetClassName}>
                {_children}
                {help}
            </div>
        );
    }
};

const fieldTemplateArray = (id, label, required, description, formContext, help, _children) => {
    // By default react json schema set displayLabel to false for array field. Here we force it to true by not taking it into account
    return (
        <div className={'bx--fieldset baiw--form-array-field'}>
            <div className={'baiw--form-array-field-header'}>
                {label && <FormLabel>{label}{!required && formContext && formContext.optionalLabel ? ` (${formContext.optionalLabel})` : null}</FormLabel>}
                {description && <div className="bx--form__helper-text" htmlFor={id}>{description}</div>}
            </div>
            {_children}
            {help}
        </div>
    );
};

function CustomFieldTemplate(props) {
    const { id, label, rawErrors, help, hidden, required, schema, description, children, displayLabel, formContext } = props;

    // updateValidity function may be overridden by props
    const updatedValidity = props.updateValidity || defaultUpdateValidity;

    const [fieldSchema, setFieldSchema] = useState(schema);
    useEffect(() => {
        setFieldSchema(schema);
    }, [schema]);

    useEffect(() => {
        if (!hidden) {
            setFieldSchema(schema => updatedValidity(rawErrors, schema));
        }
    }, [hidden, rawErrors, schema, updatedValidity]);

    if (hidden) { return null; }

    const _children = React.Children.map(children, (child) => {
        if (child) {
            return React.cloneElement(child, {
                schema: fieldSchema
            });
        } else {
            return child;
        }
    });

    if (fieldSchema.type === 'object') {
        return fieldTemplateObject(id, label, required, description, formContext, help, _children, fieldSchema);
    }

    if (fieldSchema.type === 'array') {
        return fieldTemplateArray(id, label, required, description, formContext, help, _children);
    }

    return (
        <div className={'bx--fieldset'}>
            {displayLabel && label && <FormLabel >{label}{!required && formContext && formContext.optionalLabel ? ` (${formContext.optionalLabel})` : null}</FormLabel>}
            {description && <div className="bx--form__helper-text" htmlFor={id}>{description}</div>}
            {_children}
            {help}
        </div>
    );
}

export { CustomFieldTemplate };
